(() => {
  if (window.vivilandPdpComponentReady) return;
  window.vivilandPdpComponentReady = true;
  let controller, readVersion = 0, adding = false, observer, trigger, imageStream, galleryStream, retrySelection;
  let selectedOptions = [], initializedPage;
  const optionRequestTimeout = 12000;
  function createGallery(page) {
    const gallery = page.querySelector('.vp-gallery');
    const stage = gallery?.querySelector('.vp-main-image');
    const initial = stage?.querySelector('img');
    const photos = [...(gallery?.querySelectorAll('[data-vp-photo]') || [])];
    if (!initial || !photos.length) return null;
    const listeners = new AbortController();
    const cache = new Map();
    const widths = [400, 600, 800, 1100, 1400, 1600];
    const baseAlt = initial.alt;
    let selected = photos.find(photo => photo.getAttribute('aria-pressed') === 'true') || photos[0];
    let version = 0, destroyed = false, foreground, background = false, idle, resizeTimer;
    let queue = [], displayedKey = '';
    const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: listeners.signal });
    const imageWidth = () => Math.max(1, Math.ceil(stage.getBoundingClientRect().width));
    const keyFor = photo => {
      const required = imageWidth() * (window.devicePixelRatio || 1);
      return `${photo.dataset.photoSrc}|${widths.find(width => width >= required) || widths[widths.length - 1]}`;
    };
    const trim = () => {
      for (const [key, entry] of cache) {
        if (cache.size <= 6) break;
        if (key !== displayedKey && entry !== foreground && entry.ready) cache.delete(key);
      }
    };
    const cancelIdle = () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      clearTimeout(idle);
    };
    const canPrefetch = () => {
      const connection = navigator.connection;
      return !destroyed && document.visibilityState !== 'hidden' && !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '');
    };
    const prepare = (photo, priority = 'low') => {
      const key = keyFor(photo);
      const existing = cache.get(key);
      if (existing) {
        cache.delete(key); cache.set(key, existing);
        if (priority === 'high') existing.image.fetchPriority = 'high';
        return existing;
      }
      const source = photo.querySelector('[data-vp-photo-image]')?.content.querySelector('img');
      const image = new Image();
      const entry = { image, key, ready: false, cancel: null, promise: null };
      cache.set(key, entry);
      entry.promise = new Promise(resolve => {
        let settled = false;
        const finish = ready => {
          if (settled) return;
          settled = true;
          image.onload = image.onerror = null;
          entry.ready = ready;
          if (!ready && cache.get(key) === entry) cache.delete(key);
          resolve(ready ? image : null);
          trim();
        };
        entry.cancel = () => {
          finish(false);
          image.removeAttribute('srcset'); image.removeAttribute('src');
        };
        image.onload = async () => {
          try {
            if (image.decode) await image.decode();
            finish(image.naturalWidth > 0);
          } catch { finish(false); }
        };
        image.onerror = () => finish(false);
        image.alt = baseAlt;
        image.width = Number(photo.dataset.photoWidth);
        image.height = Number(photo.dataset.photoHeight);
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = priority;
        image.sizes = `${imageWidth()}px`;
        if (source?.getAttribute('style')) image.setAttribute('style', source.getAttribute('style'));
        if (source?.srcset) image.srcset = source.srcset;
        image.src = source?.getAttribute('src') || photo.dataset.photoSrc;
      });
      return entry;
    };
    const pump = () => {
      if (background || !canPrefetch()) return;
      const photo = queue.shift();
      if (!photo) return;
      background = true;
      prepare(photo).promise.then(() => { background = false; pump(); });
    };
    const scheduleNeighbors = () => {
      cancelIdle();
      if (!canPrefetch()) return;
      const run = () => {
        if (!canPrefetch()) return;
        const index = photos.indexOf(selected);
        queue = [photos[index + 1], photos[index > 0 ? index - 1 : index + 2]].filter(Boolean);
        pump();
      };
      idle = window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 1200 }) : setTimeout(run, 250);
    };
    const commit = (entry, token) => {
      if (destroyed || token !== version || !page.isConnected) return;
      const current = stage.querySelector('img');
      if (current !== entry.image) current.replaceWith(entry.image);
      displayedKey = entry.key;
      stage.dataset.galleryState = 'ready';
      stage.setAttribute('aria-busy', 'false');
      trim();
      scheduleNeighbors();
    };
    const select = (photo, preview = true) => {
      if (destroyed || !photos.includes(photo)) return;
      selected = photo;
      const token = ++version;
      cancelIdle(); queue = [];
      photos.forEach(item => item.setAttribute('aria-pressed', String(item === photo)));
      const entry = prepare(photo, 'high');
      if (foreground && foreground !== entry && !foreground.ready) foreground.cancel?.();
      foreground = entry;
      if (entry.ready) { commit(entry, token); return; }
      stage.setAttribute('aria-busy', 'true');
      stage.dataset.galleryState = 'loading';
      const thumbnail = photo.querySelector('img');
      if (preview && thumbnail?.complete && thumbnail.naturalWidth > 0) {
        const placeholder = new Image();
        placeholder.alt = baseAlt;
        placeholder.width = Number(photo.dataset.photoWidth);
        placeholder.height = Number(photo.dataset.photoHeight);
        placeholder.loading = 'eager';
        placeholder.decoding = 'async';
        placeholder.fetchPriority = 'high';
        const showPreview = async () => {
          try { if (placeholder.decode) await placeholder.decode(); } catch { return; }
          if (destroyed || token !== version || entry.ready || !placeholder.naturalWidth || !page.isConnected) return;
          stage.querySelector('img').replaceWith(placeholder);
          if (stage.dataset.galleryState !== 'error') stage.dataset.galleryState = 'preview';
        };
        placeholder.onload = showPreview;
        placeholder.src = thumbnail.currentSrc || thumbnail.src;
        if (placeholder.complete && placeholder.naturalWidth > 0) showPreview();
      }
      entry.promise.then(image => {
        if (destroyed || token !== version || !page.isConnected) return;
        if (image) commit(entry, token);
        else { stage.dataset.galleryState = 'error'; stage.setAttribute('aria-busy', 'false'); }
      });
    };
    const warm = event => {
      if (event.pointerType === 'touch' || !canPrefetch()) return;
      const photo = event.target.closest('[data-vp-photo]');
      if (!photos.includes(photo) || photo === selected) return;
      queue = [photo, ...queue.filter(item => item !== photo)].slice(0, 2);
      pump();
    };
    listen(gallery, 'pointerover', warm);
    listen(gallery, 'focusin', warm);
    listen(window, 'resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!destroyed && keyFor(selected) !== displayedKey) select(selected, false);
      }, 150);
    });
    const initialEntry = { image: initial, key: keyFor(selected), ready: false, promise: null };
    displayedKey = initialEntry.key;
    stage.dataset.galleryState = 'loading';
    initialEntry.promise = new Promise(resolve => {
      const ready = async () => {
        try { if (initial.decode) await initial.decode(); initialEntry.ready = initial.naturalWidth > 0; } catch { /* A failed first image must not block the gallery. */ }
        if (!initialEntry.ready && cache.get(initialEntry.key) === initialEntry) cache.delete(initialEntry.key);
        resolve(initialEntry.ready ? initial : null);
        if (!destroyed && version === 0) {
          stage.dataset.galleryState = initialEntry.ready ? 'ready' : 'error';
          scheduleNeighbors();
        }
      };
      if (initial.complete) ready();
      else { listen(initial, 'load', ready); listen(initial, 'error', ready); }
    });
    cache.set(initialEntry.key, initialEntry);
    return {
      select,
      destroy() {
        destroyed = true; ++version;
        cancelIdle(); clearTimeout(resizeTimer);
        listeners.abort(); queue = [];
        cache.forEach(entry => { if (!entry.ready) entry.cancel?.(); });
        cache.clear();
      }
    };
  }
  function createImageStream(dialog) {
    const stage = dialog?.querySelector('[data-vp-image-stream]');
    if (!stage) return null;
    let frame = 0;
    return {
      open() {
        const page = dialog.closest('[data-vp]');
        const main = page?.querySelector('.vp-main-image>img');
        const photos = [...(page?.querySelectorAll('.vp-gallery [data-vp-photo]') || [])];
        const selected = Math.max(0, photos.findIndex(photo => photo.getAttribute('aria-pressed') === 'true'));
        const sources = photos.map(photo => ({
          src: photo.dataset.photoZoomSrc || photo.dataset.photoSrc,
          width: Number(photo.dataset.photoWidth),
          height: Number(photo.dataset.photoHeight),
          alt: [main?.alt, photo.getAttribute('aria-label')].filter(Boolean).join(' — ')
        }));
        if (!sources.length && main) sources.push({ src: main.currentSrc || main.src, width: main.naturalWidth || main.width, height: main.naturalHeight || main.height, alt: main.alt });
        const images = sources.map((source, index) => {
          const image = document.createElement('img');
          image.alt = source.alt || '';
          if (source.width > 0 && source.height > 0) { image.width = source.width; image.height = source.height; }
          image.loading = index === selected ? 'eager' : 'lazy';
          image.decoding = 'async';
          image.draggable = false;
          image.sizes = '(min-width: 1000px) 1000px, 100vw';
          const url = new URL(source.src, location.href);
          const widths = [480, 900, 1400, 2000, 3000].filter(width => !source.width || width < source.width);
          if (source.width) widths.push(Math.min(source.width, 3000));
          image.srcset = [...new Set(widths)].map(width => {
            const candidate = new URL(url.href); candidate.searchParams.set('width', String(width));
            return `${candidate.href} ${width}w`;
          }).join(', ');
          image.src = url.href;
          return image;
        });
        stage.replaceChildren(...images);
        stage.scrollTop = 0;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (!dialog.open) return;
          stage.scrollTop = images[selected]?.offsetTop || 0;
          stage.focus({ preventScroll: true });
        });
      },
      destroy() { cancelAnimationFrame(frame); }
    };
  }
  const root = () => document.querySelector('[data-vp]');
  const status = (text) => { const node = root()?.querySelector('[data-vp-status]'); if(node){node.textContent=text;node.hidden=!text;} };
  const init = () => {
    const page=root(); if(page===initializedPage)return;
    controller?.abort(); controller = null; ++readVersion; retrySelection = null;
    observer?.disconnect();
    imageStream?.destroy();
    galleryStream?.destroy();
    initializedPage=page; if(!page) return;
    selectedOptions = [...page.querySelectorAll('[data-vp-option]:checked')].map(input => input.id);
    imageStream = createImageStream(page.querySelector('.vp-zoom'));
    galleryStream = createGallery(page);
    const primary=page.querySelector('[data-vp-primary]');
    observer=new IntersectionObserver(entries=>{const out=entries[0];page.querySelector('[data-vp-sticky]').hidden=out.isIntersecting||(out.boundingClientRect.top>0&&window.scrollY<180);},{threshold:0});
    observer.observe(primary);
    if (matchMedia('(max-width:899px)').matches) page.querySelectorAll('.vp-options').forEach(group=>{const strip=group.querySelector('.vp-values'), selected=group.querySelector('input:checked')?.closest('label');if(strip&&selected){strip.scrollLeft+=selected.getBoundingClientRect().left-strip.getBoundingClientRect().left-(strip.clientWidth-selected.offsetWidth)/2;}});
    window.Shopify?.PaymentButton?.init();
  };
  const lock = (busy, page = root(), lockOptions = true) => {
    if (!page) return;
    page.setAttribute('aria-busy',String(busy));const payment=page.querySelector('.vp-payment');if(payment)payment.inert=busy || page.dataset.purchaseBlocked === 'true' || payment.dataset.unavailable === 'true';
    page.querySelectorAll('[data-vp-option],.vp-quantity input,.vp-quantity button,.vp-add,[data-vp-sticky-add],[data-vp-buy-now]').forEach(el=>{
      if(busy){if(!lockOptions&&el.matches('[data-vp-option]'))return;if(el.dataset.wasDisabled===undefined)el.dataset.wasDisabled=String(el.disabled);el.disabled=true;}
      else if(el.dataset.wasDisabled!==undefined){el.disabled=el.dataset.wasDisabled==='true';delete el.dataset.wasDisabled;}
    });
  };
  async function selectOption(input) {
    if(adding) return;
    const page=root(); if(!page||!page.contains(input))return;
    const focusId=input.id, quantity=page.querySelector('[name=quantity]').value;
    const requestedOptions=[...page.querySelectorAll('[data-vp-option]:checked')];
    const optionValues=requestedOptions.map(el=>el.dataset.valueId).join(',');
    controller?.abort(); const requestController=new AbortController(); controller=requestController; const version=++readVersion;
    let timeout;
    retrySelection=null;const retryButton=page.querySelector('[data-vp-retry-options]');if(retryButton)retryButton.hidden=true;
    status(page.dataset.loading);lock(true,page,false);
    try{
      const url=new URL(input.dataset.productUrl||page.dataset.url,location.origin);
      if(url.origin!==location.origin)throw Error();
      url.searchParams.set('section_id',page.dataset.section);url.searchParams.delete('variant');
      url.searchParams.set('option_values',optionValues);
      const text=await Promise.race([
        (async()=>{
          const response=await fetch(url.href,{credentials:'same-origin',cache:'no-store',signal:requestController.signal});
          if(!response.ok)throw Error();
          return response.text();
        })(),
        new Promise((_,reject)=>{timeout=setTimeout(()=>{requestController.abort();reject(Error());},optionRequestTimeout);})
      ]);
      if(version!==readVersion||root()!==page)return;
      const html=new DOMParser().parseFromString(text,'text/html'), next=html.querySelector('[data-vp]');
      if(!next||next.dataset.section!==page.dataset.section||!next.querySelector('[data-vp-form]')||!next.querySelector('.vp-add')||!next.querySelector('[data-vp-primary]')||!next.querySelector('[data-vp-sticky]')||!next.querySelector('[data-vp-sticky-add]'))throw Error();
      next.querySelectorAll('noscript').forEach(node=>node.remove());
      const nextValues=[...next.querySelectorAll('[data-vp-option]:checked')].map(el=>el.dataset.valueId).join(',');
      const variantInput=next.querySelector('[name=id]');
      if(nextValues!==optionValues||!variantInput||String(next.dataset.variant||'')!==variantInput.value)throw Error();
      const qty=next.querySelector('[name=quantity]');if(qty){qty.value=quantity;if(!qty.checkValidity())qty.value=qty.min||1;}
      page.replaceWith(next);
      document.getElementById(focusId)?.focus({preventScroll:true});
      const address=new URL(location.href);address.searchParams.delete('option_values');address.searchParams.delete('section_id');
      if(next.dataset.variant)address.searchParams.set('variant',next.dataset.variant);else address.searchParams.set('option_values',url.searchParams.get('option_values'));
      history.replaceState({},'',address);init();
    }catch(error){
      if(version!==readVersion||root()!==page)return;
      page.querySelectorAll('[data-vp-option]').forEach(option=>{option.checked=selectedOptions.includes(option.id);});
      retrySelection={page,input,optionIds:requestedOptions.map(option=>option.id)};
      status(page.dataset.error);if(retryButton)retryButton.hidden=false;
    }finally{
      clearTimeout(timeout);
      if(version===readVersion&&root()===page){controller=null;lock(false,page);}
    }
  }
  document.addEventListener('change',event=>{if(event.target.matches('[data-vp-option]'))selectOption(event.target);});
  document.addEventListener('click',event=>{
    const page=root();if(!page)return;
    if(event.target.closest('[data-vp-retry-options]')){
      if(retrySelection?.page===page&&!adding){
        const selection=retrySelection;
        page.querySelectorAll('[data-vp-option]').forEach(option=>{option.checked=selection.optionIds.includes(option.id);});
        selectOption(selection.input);
      }
      return;
    }
    const open=event.target.closest('[data-vp-open]');
    if(open&&page.contains(open)){trigger=open;const dialog=document.getElementById(open.dataset.vpOpen);dialog?.showModal();if(dialog?.classList.contains('vp-zoom'))imageStream?.open();return;}
    const close=event.target.closest('[data-vp-close]');
    if(close){close.closest('dialog').close();trigger?.focus({preventScroll:true});return;}
    if(event.target.matches('.vp-dialog')){const r=event.target.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)event.target.close();}
    const photo=event.target.closest('[data-vp-photo]');
    if(photo){
      galleryStream?.select(photo);
      return;
    }
    const quantity=event.target.closest('[data-vp-quantity]');
    if(quantity&&!adding){const input=page.querySelector('[name=quantity]');if(Number(quantity.dataset.vpQuantity)>0)input.stepUp();else input.stepDown();input.dispatchEvent(new Event('change',{bubbles:true}));}
    if(event.target.closest('[data-vp-sticky-add]'))page.querySelector('[data-vp-form]').requestSubmit();
  });
  document.addEventListener('submit',async event=>{
    if(!event.target.matches('[data-vp-form]'))return;
    event.preventDefault();if(adding)return;
    const page=root(), form=event.target;
    if(page.getAttribute('aria-busy')==='true'||page.dataset.purchaseBlocked==='true'||!form.reportValidity())return;
    const buyNow=!!event.submitter?.matches('[data-vp-buy-now]');
    if(buyNow&&!window.VivilandGiftCheckoutGate?.openBuyNow){status('Checkout is still loading. Please try again, or add this item to your bag.');return;}
    const data=new FormData(form), id=Number(data.get('id')), quantity=Number(data.get('quantity')), properties={};
    if(!Number.isSafeInteger(id)||id<=0||!Number.isSafeInteger(quantity)||quantity<1)return;
    for(const [key,value] of data.entries()){const match=key.match(/^properties\[(.+)\]$/);if(match)properties[match[1]]=value;}
    adding=true;lock(true);status(page.dataset.adding);const button=page.querySelector('.vp-add'), label=button.querySelector('[data-vp-add-label]'), original=label.textContent;label.textContent=page.dataset.adding;
    let confirmed=false;
    try{
      const response=await fetch(page.dataset.cartAdd,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json',...(buyNow?{'X-Viviland-Buy-Now':'1'}:{})},body:JSON.stringify({items:[{id,quantity,properties}]})});
      const result=await response.json();
      if(!response.ok){confirmed=true;throw Error(result.description||result.message||page.dataset.error);}
      const item=result.items?.find(item=>Number(item.variant_id||item.id)===id)||((Number(result.variant_id||result.id)===id)?result:null);
      if(!item)throw Error(page.dataset.unknown);
      confirmed=true;status('');
      if(buyNow)await window.VivilandGiftCheckoutGate.openBuyNow(item);
      else document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      try{const bagResponse=await fetch(page.dataset.cartUrl+'.js',{credentials:'same-origin',cache:'no-store'});if(bagResponse.ok){const bag=await bagResponse.json();document.querySelectorAll('[data-header-bag-count]').forEach(node=>{node.textContent=String(bag.item_count);node.hidden=bag.item_count===0;});const bagLink=document.querySelector('[data-vv-bag]');if(bagLink)bagLink.setAttribute('aria-label',bagLink.getAttribute('aria-label').replace(/\(\d+\)$/,`(${bag.item_count})`));}}catch{}
    }catch(error){status(confirmed?error.message:page.dataset.unknown);if(!confirmed)page.querySelector('[data-vp-check-bag]').hidden=false;}
    finally{adding=false;if(!confirmed)page.dataset.purchaseBlocked='true';lock(false);label.textContent=original;if(!confirmed){button.disabled=true;page.querySelector('[data-vp-sticky-add]').disabled=true;const buy=page.querySelector('[data-vp-buy-now]');if(buy)buy.disabled=true;}}
  });
  window.addEventListener('scroll',()=>{const page=root();if(!page)return;const bounds=page.querySelector('[data-vp-primary]').getBoundingClientRect();page.querySelector('[data-vp-sticky]').hidden=(bounds.bottom>100&&bounds.top<innerHeight)||scrollY<180;},{passive:true});
  document.addEventListener('shopify:section:load',init);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
})();

